import { Link, useParams } from "@tanstack/react-router";
import { MarkdownText } from "@web/components/MarkdownText";
import { ArrowLeft } from "@web/components/ui";
import { useShareInvite } from "@web/hooks/useShareInvite";
import { useCallback, useEffect, useState } from "react";
import { BadgesSection } from "@/components/badges";
import { CommentsSection } from "@/components/comments";
import { CompletedCompetitionView } from "@/components/completed-competition";
import { useToast } from "@/contexts";
import type { BadgeDefinitionResponse } from "@/features/badges/api";
import { getAllBadgeDefinitions } from "@/features/badges/api";
import { getCompetitionById, getDailyMessage } from "@/features/competitions/api";
import {
  createCompetitionComment,
  getCompetitionComments,
  getCompetitionMemberUsernames,
  toggleReaction,
} from "@/features/competitions/comments";
import { getCurrentUserProfile } from "@/features/dashboard/api";
import { listOrgGroups } from "@/features/organizations/groups";
import { todayInTimezone } from "@/server/helpers/general";
import { colors, LANE_COLORS, RANK_COLORS, TOP3_BG_COLORS } from "@/theme";
import type { Comment, CompetitionSummary, Group, UserProfile } from "@/types";

const RANK_EMOJIS = ["🥇", "🥈", "🥉"];

export default function CompetitionDetail() {
  const { id } = useParams({ from: "/competition/$id" }) as { id: string };
  const [competition, setCompetition] = useState<CompetitionSummary | null>(null);
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [usernames, setUsernames] = useState<string[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [badgesRefreshKey] = useState(0);
  const [allBadgeDefs, setAllBadgeDefs] = useState<BadgeDefinitionResponse[]>([]);
  const [orgGroups, setOrgGroups] = useState<Group[]>([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [dailyMessage, setDailyMessage] = useState<string | null>(null);
  const toast = useToast();
  const shareInvite = useShareInvite();

  useEffect(() => {
    if (!id) {
      setCommentsLoading(false);
      return;
    }

    (async () => {
      try {
        const [competitionResult, userResult, commentsResult, usernamesResult, badgeDefResult] =
          await Promise.all([
            getCompetitionById(id),
            getCurrentUserProfile(),
            getCompetitionComments(id),
            getCompetitionMemberUsernames(id),
            getAllBadgeDefinitions(),
          ]);
        setCompetition(competitionResult.data);
        if (userResult.data) setCurrentUser(userResult.data);
        if (commentsResult.data) setComments(commentsResult.data);
        if (usernamesResult.data) setUsernames(usernamesResult.data);
        if (badgeDefResult.data) setAllBadgeDefs(badgeDefResult.data);
        if (userResult.data?.orgId) {
          const groupsResult = await listOrgGroups(userResult.data.orgId);
          if (groupsResult.data) {
            setOrgGroups(groupsResult.data);
          }
        }
      } finally {
        setLoading(false);
        setCommentsLoading(false);
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    getDailyMessage(id).then((result) => {
      if (!cancelled && result.data) setDailyMessage(result.data.message);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleAddComment = useCallback(
    async (body: string) => {
      if (!competition) return;
      await createCompetitionComment(competition.id, body);
      const { data } = await getCompetitionComments(competition.id);
      if (data) setComments(data);
    },
    [competition?.id, competition],
  );

  const handleReply = useCallback(
    async (parentId: string, body: string) => {
      if (!competition) return;
      await createCompetitionComment(competition.id, body, parentId);
      const { data } = await getCompetitionComments(competition.id);
      if (data) setComments(data);
    },
    [competition?.id, competition],
  );

  const handleToggleReaction = useCallback(
    async (commentId: string, emoji: string) => {
      if (!competition) return;
      await toggleReaction(commentId, emoji);
      const { data } = await getCompetitionComments(competition.id);
      if (data) setComments(data);
    },
    [competition?.id, competition],
  );

  if (!competition) {
    return (
      <div className="min-h-screen bg-background">
        <div className="px-4 pt-5">
          <Link
            to="/dashboard"
            className="w-7 h-7 rounded-full border border-text flex items-center justify-center bg-white no-underline hover:bg-card-strong transition-colors"
          >
            <ArrowLeft />
          </Link>
        </div>
        <div className="min-h-screen flex items-center justify-center p-5 gap-2 bg-background">
          {loading ? (
            <span className="text-text-muted text-[1rem]">Loading...</span>
          ) : (
            <>
              <span className="text-[2.5rem] mb-2" role="img" aria-label="Not found">
                🔍
              </span>
              <span className="text-[1.25rem] font-extrabold text-text">Competition not found</span>
              <span className="text-[0.875rem] leading-5 text-center text-text-muted max-w-[20rem]">
                That invite doesn't match an active competition.
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  const topSteps = competition.leaderboard[0]?.steps ?? 0;
  const currentUserEntry = competition.leaderboard.find(
    (entry) => entry.userId === currentUser?.id,
  );
  const currentUserSteps = currentUserEntry?.steps ?? 0;
  const todayInCompTz = todayInTimezone(competition.timezone || "America/Los_Angeles");
  const isCompleted = competition.endsAt < todayInCompTz;

  const typeLabels: Record<string, string> = {
    step_race: "Step race",
    target_race: "Target race",
    daily_goals: "Daily goals",
  };
  const fmtShort = (s: string) =>
    new Date(`${s}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const totalDays =
    competition.startsAt && competition.endsAt
      ? Math.max(
          1,
          Math.round(
            (new Date(competition.endsAt).getTime() - new Date(competition.startsAt).getTime()) /
              (1000 * 60 * 60 * 24),
          ) + 1,
        )
      : 0;
  const reachedTargetCount = competition.leaderboard.filter((e) => e.reachedTarget).length;

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-background relative">
        <div
          className="absolute top-[6.25rem] right-[-3.75rem] w-[13.75rem] h-[13.75rem] rounded-full bg-[rgba(32,191,107,0.08)]"
          aria-hidden="true"
        />
        <div
          className="absolute bottom-[6.25rem] left-[-2.5rem] w-[8.75rem] h-[8.75rem] rounded-full bg-[rgba(156,77,255,0.07)]"
          aria-hidden="true"
        />
        <div className="px-4 pt-5">
          <Link
            to="/dashboard"
            className="w-7 h-7 rounded-full border border-text flex items-center justify-center bg-white no-underline hover:bg-card-strong transition-colors"
          >
            <ArrowLeft />
          </Link>
        </div>
        <CompletedCompetitionView competition={competition} currentUserId={currentUser?.id ?? ""} />
      </div>
    );
  }

  const top3BgColors = TOP3_BG_COLORS;

  return (
    <div className="min-h-screen bg-background relative">
      <div
        className="fixed top-[-5rem] right-[-3.75rem] w-[13.75rem] h-[13.75rem] rounded-full bg-[rgba(32,191,107,0.08)] pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="fixed bottom-[6.25rem] left-[-2.5rem] w-[8.75rem] h-[8.75rem] rounded-full bg-[rgba(156,77,255,0.07)] pointer-events-none"
        aria-hidden="true"
      />
      <div className="flex flex-col p-5 gap-4 min-h-screen max-w-3xl mx-auto w-full">
        {competition.deletedAt && (
          <div className="rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-[rgba(255,59,48,0.08)] p-4 gap-1">
            <span className="text-coral text-[0.9375rem] font-black uppercase">
              This competition has been deleted
            </span>
            <span className="text-text-muted text-[0.8125rem]">
              It is no longer active and cannot be modified.
            </span>
          </div>
        )}

        <div className="flex flex-col rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card-strong p-[1.375rem] gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Link
                to="/dashboard"
                className="w-7 h-7 rounded-full border border-text flex items-center justify-center bg-white no-underline shrink-0 hover:bg-card-strong transition-colors"
              >
                <ArrowLeft />
              </Link>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-aqua border border-text shrink-0" />
                <span className="text-text font-black text-xs uppercase tracking-wider whitespace-nowrap">
                  {typeLabels[competition.type] ?? "Competition"}
                </span>
              </div>
            </div>
            <span className="text-text-muted text-[0.875rem] leading-5 font-bold whitespace-nowrap">
              {fmtShort(competition.startsAt)} – {fmtShort(competition.endsAt)}
              {competition.type === "daily_goals" && ` (${totalDays} days)`}
            </span>
          </div>
          <span className="text-text text-[1.875rem] font-black tracking-[-0.0313em]">
            {competition.name}
          </span>
          {competition.type === "target_race" && competition.targetSteps && (
            <span className="text-text-muted text-[0.875rem] leading-5 font-bold">
              <span role="img" aria-label="Target">
                🎯
              </span>{" "}
              First to {competition.targetSteps.toLocaleString()} steps
            </span>
          )}
          {competition.type === "daily_goals" && competition.dailyGoalSteps && (
            <span className="text-text-muted text-[0.875rem] leading-5 font-bold">
              <span role="img" aria-label="Target">
                🎯
              </span>{" "}
              Hit {competition.dailyGoalSteps.toLocaleString()} steps/day
            </span>
          )}
        </div>

        <div className="flex gap-3">
          <div className="flex flex-col flex-1 rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4 gap-1">
            <span className="text-[1.25rem]" role="img" aria-label="Members">
              👥
            </span>
            <span className="text-text text-2xl font-black">{competition.memberCount}</span>
            <span className="text-text-dim text-[0.8125rem] font-bold uppercase tracking-wide">
              Members
            </span>
          </div>
          {competition.type === "target_race" ? (
            <div className="flex flex-col flex-1 rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4 gap-1">
              <span className="text-[1.25rem]" role="img" aria-label="Target">
                🎯
              </span>
              <span className="text-text text-2xl font-black">{reachedTargetCount}</span>
              <span className="text-text-dim text-[0.8125rem] font-bold uppercase tracking-wide">
                Reached target
              </span>
            </div>
          ) : competition.type === "daily_goals" ? (
            <div className="flex flex-col flex-1 rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4 gap-1">
              <span className="text-[1.25rem]" role="img" aria-label="Total days">
                📅
              </span>
              <span className="text-text text-2xl font-black">{totalDays}</span>
              <span className="text-text-dim text-[0.8125rem] font-bold uppercase tracking-wide">
                Total days
              </span>
            </div>
          ) : (
            <div className="flex flex-col flex-1 rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4 gap-1">
              <span className="text-[1.25rem]" role="img" aria-label="Leading steps">
                👟
              </span>
              <span className="text-text text-2xl font-black">{topSteps.toLocaleString()}</span>
              <span className="text-text-dim text-[0.8125rem] font-bold uppercase tracking-wide">
                Leading steps
              </span>
            </div>
          )}
        </div>

        {dailyMessage && (
          <div className="rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4">
            <span className="text-text-muted text-[0.75rem] font-bold uppercase tracking-wider">
              💬 Daily Update
            </span>
            <MarkdownText
              text={dailyMessage}
              className="text-text text-[0.9375rem] mt-1 leading-relaxed"
            />
            <button
              type="button"
              onClick={async () => {
                const top5 = competition.leaderboard.slice(0, 5);
                const lb = top5
                  .map((e, i) => `  #${i + 1}: ${e.name} — ${e.steps.toLocaleString()} steps`)
                  .join("\n");
                const text = `🏆 FootSlapping Daily Update: ${competition.name}\n\n${dailyMessage}\n\nLeaderboard:\n${lb}\n\nmade with 🐾 and ❤️ by FootSlapping\nhttps://footslapping.com`;
                const url = window.location.href;
                if (navigator.share) {
                  try {
                    await navigator.share({
                      title: `FootSlapping: ${competition.name}`,
                      text,
                      url,
                    });
                  } catch {}
                } else {
                  try {
                    await navigator.clipboard.writeText(`${text}\n\n${url}`);
                    toast.showAlert("Copied!", "Daily summary copied to clipboard", "success");
                  } catch {
                    toast.showAlert("Daily summary", text, "info");
                  }
                }
              }}
              className="mt-3 w-full md:w-auto bg-white border border-text rounded-2xl py-2.5 px-4 shadow-[1px_1px_0px_0px_#1c1b2e]"
            >
              <span className="text-text text-[0.8125rem] font-black inline-flex items-center gap-1.5">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
                Share summary
              </span>
            </button>
          </div>
        )}

        <RaceProgress
          leaderboard={competition.leaderboard}
          currentUserId={currentUser?.id ?? ""}
          badgePool={competition.badgePool ?? []}
          allBadgeDefs={allBadgeDefs}
          competitionType={competition.type}
          targetSteps={competition.targetSteps ?? undefined}
          totalDays={totalDays}
        />

        <div className="flex flex-col lg:flex-row lg:gap-6 gap-[1.125rem]">
          <div className="flex flex-col min-w-0 gap-3 flex-1">
            <div className="flex items-center justify-between">
              <span className="text-text text-2xl font-black tracking-[-0.0187em]">
                Leaderboard
              </span>
              {orgGroups.length > 0 && competition.groupIds && competition.groupIds.length > 0 && (
                <select
                  value={groupFilter}
                  onChange={(e) => setGroupFilter(e.target.value)}
                  className="rounded-[12px] border border-[rgba(28,27,46,0.2)] px-3 py-1.5 text-text text-[0.75rem] font-bold bg-white outline-none"
                >
                  <option value="">All Groups</option>
                  {orgGroups
                    .filter((g) => competition.groupIds?.includes(g.id))
                    .map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                </select>
              )}
            </div>
            {competition.leaderboard
              .filter((entry) => {
                if (!groupFilter) return true;
                return (entry as { groupId?: string }).groupId === groupFilter;
              })
              .map((entry, index) => {
                const rowBg = index < 3 ? top3BgColors[index] : "#ffffff";
                return (
                  <div
                    key={entry.userId}
                    className="rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] p-[0.875rem] flex gap-3 items-center mb-2"
                    style={{ backgroundColor: rowBg }}
                  >
                    <div
                      className="w-[2.625rem] h-[2.625rem] rounded-[21px] flex items-center justify-center border border-text shadow-[1px_1px_0px_0px_#1c1b2e]"
                      style={{
                        backgroundColor: index < 3 ? RANK_COLORS[index] : colors.rankBg,
                      }}
                    >
                      <span
                        className={
                          index < 3
                            ? "text-[1.25rem]"
                            : "text-text-muted text-[0.875rem] font-black"
                        }
                      >
                        {index < 3 ? (
                          <span
                            role="img"
                            aria-label={["1st place", "2nd place", "3rd place"][index]}
                          >
                            {RANK_EMOJIS[index]}
                          </span>
                        ) : (
                          `#${index + 1}`
                        )}
                      </span>
                    </div>
                    <div className="flex flex-col flex-1 gap-0.5">
                      <span className="text-text text-base font-black">{entry.name}</span>
                      {competition.type === "daily_goals" ? (
                        <span className="text-text-muted text-[0.8125rem]">
                          {entry.daysAchieved ?? 0} of {totalDays} days —{" "}
                          {entry.steps.toLocaleString()} total steps
                        </span>
                      ) : competition.type === "target_race" ? (
                        <span className="text-text-muted text-[0.8125rem]">
                          {entry.steps.toLocaleString()} /{" "}
                          {competition.targetSteps?.toLocaleString() ?? "?"} steps
                          {entry.reachedTarget ? (
                            <span role="img" aria-label="Reached target">
                              {" "}
                              ✅
                            </span>
                          ) : (
                            ""
                          )}
                        </span>
                      ) : (
                        <span className="text-text-muted text-[0.8125rem]">
                          {entry.steps.toLocaleString()} total steps
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-text-dim text-xs font-black uppercase tracking-wide ${index === 0 ? "text-aqua" : ""}`}
                    >
                      {index === 0 ? "Leader" : `#${index + 1}`}
                    </span>
                  </div>
                );
              })}

            <BadgesSection
              competitionId={competition.id}
              currentUserId={currentUser?.id ?? ""}
              currentSteps={currentUserSteps}
              refreshKey={badgesRefreshKey}
            />
          </div>

          <div className="flex flex-col lg:w-[23.75rem] lg:flex-shrink-0 gap-[1.125rem]">
            {!competition.deletedAt && currentUser?.orgId && (
              <div className="flex flex-col gap-2.5">
                <span className="text-text text-2xl font-black tracking-[-0.0187em]">Teams</span>
                <Link to={`/competition/${id}/teams`} className="block">
                  <div className="flex flex-col rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4 gap-1">
                    <div className="flex items-center gap-2">
                      <span className="text-text text-base font-black">Manage teams</span>
                      {competition.teamScoring === "average" ? (
                        <span className="bg-aqua text-white text-[0.625rem] font-extrabold uppercase tracking-wide rounded-full px-2 py-0.5">
                          Avg mode
                        </span>
                      ) : (
                        <span className="bg-[rgba(28,27,46,0.06)] text-text-muted text-[0.625rem] font-extrabold uppercase tracking-wide rounded-full px-2 py-0.5">
                          Sum mode
                        </span>
                      )}
                    </div>
                    <span className="text-text-muted text-[0.8125rem]">
                      Create, join, and view teams →
                    </span>
                  </div>
                </Link>
              </div>
            )}

            {!competition.deletedAt && (
              <div className="flex flex-col gap-2.5">
                <span className="text-text text-2xl font-black tracking-[-0.0187em]">
                  Administration
                </span>
                <Link to={`/competition/${id}/admin`} className="block">
                  <div className="flex flex-col rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4 gap-1">
                    <span className="text-text text-base font-black">Manage members</span>
                    <span className="text-text-muted text-[0.8125rem]">
                      Make admin, demote, remove →
                    </span>
                  </div>
                </Link>
                <Link to={`/competition/${id}/settings`} className="block">
                  <div className="flex flex-col rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4 gap-1">
                    <span className="text-text text-base font-black">Competition settings</span>
                    <span className="text-text-muted text-[0.8125rem]">
                      Name, description, visibility →
                    </span>
                  </div>
                </Link>
              </div>
            )}

            {!competition.deletedAt && (
              <div className="gap-2.5">
                <span className="text-text text-2xl font-black tracking-[-0.0187em]">
                  Invite people
                </span>
                <div className="flex flex-col rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-card p-4 gap-4">
                  <div className="flex items-center gap-2 bg-white border border-[rgba(28,27,46,0.2)] rounded-lg py-2.5 px-3.5 shadow-[1px_1px_0px_0px_#1c1b2e]">
                    <span className="text-lime text-[1.125rem] font-black tracking-widest flex-1">
                      {competition.inviteCode}
                    </span>
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = await shareInvite.copyCode(competition.inviteCode);
                        if (ok)
                          toast.showAlert("Copied", "Invite code copied to clipboard", "success");
                        else toast.showAlert("Invite code", competition.inviteCode, "info");
                      }}
                      className="bg-aqua rounded-2xl px-3 py-1.5 shadow-[1px_1px_0px_0px_#1c1b2e]"
                    >
                      <span className="text-white text-xs font-black">Copy</span>
                    </button>
                  </div>

                  {/* Share style picker */}
                  <div className="flex gap-1.5">
                    {(["friendly", "trash-talk", "mystery"] as const).map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => shareInvite.setStyle(s)}
                        className={`flex-1 rounded-xl py-1.5 text-[0.6875rem] font-bold transition-all ${
                          shareInvite.style === s
                            ? "bg-white border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e]"
                            : "bg-[rgba(28,27,46,0.05)]"
                        }`}
                      >
                        {s === "friendly" ? (
                          <>
                            <span role="img" aria-label="Friendly">
                              🤝
                            </span>{" "}
                            Friendly
                          </>
                        ) : s === "trash-talk" ? (
                          <>
                            <span role="img" aria-label="Smack talk">
                              🔥
                            </span>{" "}
                            Smack talk
                          </>
                        ) : (
                          <>
                            <span role="img" aria-label="Mystery">
                              🎁
                            </span>{" "}
                            Mystery
                          </>
                        )}
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={async () => {
                      const displayName = currentUser
                        ? `${currentUser.firstName} ${currentUser.lastName}`.trim() ||
                          currentUser.username
                        : "Someone";
                      const { shared, method } = await shareInvite.share({
                        competitionName: competition.name,
                        inviterName: displayName,
                        inviteCode: competition.inviteCode,
                        inviterId: currentUser?.id ?? "",
                      });
                      if (shared) {
                        if (method === "web-share") {
                          toast.showAlert("Sent!", "Invite shared successfully", "success");
                        } else if (method === "clipboard") {
                          toast.showAlert(
                            "Copied",
                            "Invite and message copied to clipboard",
                            "success",
                          );
                        }
                      }
                    }}
                    className="bg-white border border-[rgba(28,27,46,0.2)] rounded-2xl py-2.5 text-center shadow-[1px_1px_0px_0px_#1c1b2e] w-full"
                  >
                    <span className="text-text text-[0.875rem] font-black">
                      {shareInvite.style === "friendly" ? (
                        <>
                          <span role="img" aria-label="Challenge">
                            🚀
                          </span>{" "}
                          Challenge a friend
                        </>
                      ) : shareInvite.style === "trash-talk" ? (
                        <>
                          <span role="img" aria-label="Talk smack">
                            🔥
                          </span>{" "}
                          Talk smack
                        </>
                      ) : (
                        <>
                          <span role="img" aria-label="Surprise">
                            🎁
                          </span>{" "}
                          Surprise them
                        </>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {!competition.deletedAt && (
              <CommentsSection
                comments={comments}
                usernames={usernames}
                currentUserId={currentUser?.id ?? ""}
                onAddComment={handleAddComment}
                onReply={handleReply}
                onToggleReaction={handleToggleReaction}
                loading={commentsLoading}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function getRaceLaneIndices(
  leaderboardLength: number,
  currentIndex: number,
  maxLanes = 6,
): number[] {
  const indices = new Set<number>();

  indices.add(0);
  if (leaderboardLength > 1) indices.add(1);

  if (currentIndex > 1) {
    indices.add(currentIndex);
    let offset = 1;
    while (indices.size < Math.min(maxLanes, leaderboardLength)) {
      const above = currentIndex - offset;
      const below = currentIndex + offset;
      let added = false;

      if (above > 1 && !indices.has(above)) {
        indices.add(above);
        added = true;
      }
      if (below < leaderboardLength && !indices.has(below)) {
        indices.add(below);
        added = true;
      }
      if (!added) break;
      offset++;
    }
  } else {
    for (let i = 2; i < Math.min(maxLanes, leaderboardLength); i++) {
      indices.add(i);
    }
  }

  return [...indices].sort((a, b) => a - b);
}

function RaceProgress({
  leaderboard,
  currentUserId,
  badgePool,
  allBadgeDefs,
  competitionType,
  targetSteps,
  totalDays,
}: {
  leaderboard: CompetitionSummary["leaderboard"];
  currentUserId: string;
  badgePool: CompetitionSummary["badgePool"];
  allBadgeDefs: BadgeDefinitionResponse[];
  competitionType?: string;
  targetSteps?: number;
  totalDays?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const isTargetRace = competitionType === "target_race";
  const isDailyGoals = competitionType === "daily_goals";
  const maxSteps =
    isTargetRace && targetSteps ? targetSteps : Math.max(leaderboard[0]?.steps ?? 0, 1);
  const leaderSteps = leaderboard[0]?.steps ?? 0;
  const currentIndex = leaderboard.findIndex((entry) => entry.userId === currentUserId);
  const currentEntry = currentIndex >= 0 ? leaderboard[currentIndex] : null;
  const nextEntry = currentIndex > 0 ? leaderboard[currentIndex - 1] : null;
  const leadDelta = currentEntry ? leaderSteps - currentEntry.steps : 0;
  const nextDelta = currentEntry && nextEntry ? nextEntry.steps - currentEntry.steps : 0;

  const laneIndices = expanded
    ? leaderboard.map((_, i) => i)
    : getRaceLaneIndices(leaderboard.length, currentIndex);

  const lanes: React.ReactNode[] = [];
  for (let i = 0; i < laneIndices.length; i++) {
    const idx = laneIndices[i];
    const prev = i > 0 ? laneIndices[i - 1] : undefined;

    if (prev !== undefined && idx - prev > 1) {
      const hidden = idx - prev - 1;
      lanes.push(
        <div key={`gap-${prev}-${idx}`} className="flex items-center gap-2 py-0.5">
          <div className="flex-1 h-px bg-[rgba(28,27,46,0.1)]" />
          <span className="text-text-dim text-[0.6875rem] font-bold whitespace-nowrap">
            {hidden} {hidden === 1 ? "racer" : "racers"} hidden
          </span>
          <div className="flex-1 h-px bg-[rgba(28,27,46,0.1)]" />
        </div>,
      );
    }

    const entry = leaderboard[idx];
    const isCurrentUser = entry.userId === currentUserId;
    const percent = isDailyGoals
      ? totalDays && totalDays > 0
        ? Math.max(2, Math.min(100, ((entry.daysAchieved ?? 0) / totalDays) * 100))
        : 0
      : Math.max(7, Math.min(100, (entry.steps / maxSteps) * 100));
    const color = LANE_COLORS[idx % LANE_COLORS.length];
    const competitionBadges = badgePool?.length
      ? badgePool
      : allBadgeDefs.map((b) => ({
          badgeId: b.badgeId,
          name: b.name,
          description: b.description,
          iconEmoji: b.iconEmoji,
          stepThreshold: b.stepThreshold,
          earned: false,
        }));
    const earnedBadges = competitionBadges
      .filter((b) => b.stepThreshold <= entry.steps)
      .sort((a, b) => b.stepThreshold - a.stepThreshold);
    const topBadge = earnedBadges[0];

    lanes.push(
      <div key={entry.userId} className="gap-1.5">
        <div className="flex justify-between gap-2.5 items-center">
          <div className="flex items-center gap-1.5 flex-1 min-w-0">
            <span
              className={`text-[0.8125rem] font-black text-text truncate ${isCurrentUser ? "text-coral" : ""}`}
            >
              {isCurrentUser ? "You" : entry.name}
            </span>
            {isTargetRace && entry.reachedTarget && (
              <span className="text-[0.6875rem] font-semibold text-lime whitespace-nowrap flex-shrink-0">
                <span role="img" aria-label="Reached target">
                  ✅
                </span>{" "}
                Target
              </span>
            )}
            {topBadge && !isTargetRace && (
              <span className="text-[0.6875rem] font-semibold text-lime whitespace-nowrap flex-shrink-0">
                <span role="img" aria-label={topBadge.name}>
                  {topBadge.iconEmoji}
                </span>{" "}
                {topBadge.name}
              </span>
            )}
          </div>
          {isDailyGoals ? (
            <span className="text-text-dim text-[0.75rem] font-bold">
              {entry.daysAchieved ?? 0}/{totalDays} days
            </span>
          ) : (
            <span className="text-text-dim text-[0.75rem] font-bold">
              {entry.steps.toLocaleString()}
            </span>
          )}
        </div>
        <div className="h-[1.75rem] bg-white border border-[rgba(28,27,46,0.2)] rounded-[14px] overflow-hidden relative">
          <div
            className="h-full rounded-[12px]"
            style={{ width: `${percent}%`, backgroundColor: color }}
          />
        </div>
      </div>,
    );
  }

  return (
    <div className="rounded-[20px] border border-[rgba(28,27,46,0.2)] shadow-[1px_1px_0px_0px_#1c1b2e] bg-[#f7fbff] p-4 gap-3.5">
      <div className="flex justify-between items-start gap-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-text text-2xl font-black tracking-[-0.0187em]">Race track</span>
          <span className="text-text-muted text-[0.8125rem] font-bold">
            {isDailyGoals
              ? totalDays
                ? `Hit your daily goal (${leaderboard[0]?.daysAchieved ?? 0}/${totalDays} days best)`
                : "Track your daily goal streak"
              : isTargetRace && targetSteps
                ? currentEntry
                  ? currentIndex === 0
                    ? "You are leading the race!"
                    : `${nextDelta.toLocaleString()} steps to catch ${nextEntry?.name ?? "next racer"}`
                  : "Sync steps to enter lane."
                : currentEntry
                  ? currentIndex === 0
                    ? "You are setting the pace. Feet go brrr."
                    : `${nextDelta.toLocaleString()} steps to catch ${nextEntry?.name ?? "next racer"}`
                  : "Sync steps to enter lane."}
          </span>
        </div>
        {currentEntry && (
          <div className="bg-white border border-text rounded-lg px-3 py-2 shadow-[1px_1px_0px_0px_#1c1b2e]">
            <span className="text-coral text-[1.125rem] font-black">#{currentIndex + 1}</span>
            <span className="text-text-dim text-[0.625rem] font-black uppercase tracking-wide block">
              Your place
            </span>
          </div>
        )}
      </div>

      <div className="gap-2.5">{lanes}</div>

      {isTargetRace && targetSteps && currentEntry && (
        <div className="bg-white border border-text rounded-lg p-2.5 shadow-[1px_1px_0px_0px_#1c1b2e] mt-3">
          <span className="text-text text-[0.8125rem] font-black">
            {currentEntry.reachedTarget ? (
              <>
                <span role="img" aria-label="Target reached">
                  🎯
                </span>{" "}
                Target reached!
              </>
            ) : (
              `${(targetSteps - currentEntry.steps).toLocaleString()} steps to target`
            )}
          </span>
        </div>
      )}
      {!isTargetRace && currentEntry && leadDelta > 0 && (
        <div className="bg-white border border-text rounded-lg p-2.5 shadow-[1px_1px_0px_0px_#1c1b2e] mt-3">
          <span className="text-text text-[0.8125rem] font-black">
            {leadDelta.toLocaleString()} steps behind leader
          </span>
        </div>
      )}

      {leaderboard.length > 6 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full text-center text-text-dim text-[0.75rem] font-black uppercase tracking-wide py-2 hover:text-text transition-colors"
        >
          {expanded ? "Show fewer lanes" : `Show all ${leaderboard.length} racers`}
        </button>
      )}
    </div>
  );
}
